import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";

const state = () => ({
    rules: [],
    singleRule: null,
    loading: false,
    error: null,
    totalRules: 0,
    schedulingPreferences: null,
});

const mutations = {
    SET_RULES(state, payload) {
        state.rules = payload || [];
    },
    SET_SINGLE_RULE(state, payload) {
        state.singleRule = payload;
    },
    SET_LOADING(state, value) {
        state.loading = value;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_TOTAL_RULES(state, count) {
        state.totalRules = count || 0;
    },
    SET_SCHEDULING_PREFERENCES(state, preferences) {
        state.schedulingPreferences = preferences;
    },
};

const actions = {
    async fetchSchedulingPreferences({ commit }, { personId, locationId, peopleGroupId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.get(`/users/${personId}/scheduling-rules/${locationId}`, {
                params: peopleGroupId ? { people_group_id: peopleGroupId } : {},
            });
            const output = data?.output ?? {};
            commit("SET_RULES", output);
            // Store total_rules from scheduling_preferences
            const totalRules = output?.scheduling_preferences?.total_rules || 0;
            commit("SET_TOTAL_RULES", totalRules);
            commit("SET_SCHEDULING_PREFERENCES", output?.scheduling_preferences || null);
            return output;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to fetch rules")
                : error.message || "Failed to fetch rules";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async saveRule({ commit }, { personid, locationid, people_group_id, rule }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.post(
                "/manageuserajax",
                {
                    cmd: "saverule",
                    personid,
                    locationid,
                    people_group_id,
                    rule
                }
            );

            const saved = data?.data ?? data?.output ?? data ?? {};
            commit("SET_SINGLE_RULE", saved);
            return saved;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to save rule")
                : error.message || "Failed to save rule";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async saveAllRules({ dispatch }, { personid, locationid, people_group_id, rules }) {
        // `rules` is an array of { rulenumber, ruleinfoarray }
        return Promise.all(
            rules.map((rule) =>
                dispatch("saveRule", {
                    personid,
                    locationid,
                    people_group_id,
                    rulenumber: rule.rulenumber,
                    ruleinfoarray: rule.ruleinfoarray,
                })
            )
        );
    },

    async removeRule({ commit }, { personid, locationid, people_group_id, ruleidsarray }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.post("/manageuserajax", {
                cmd: "removerule",
                personid,
                locationid,
                people_group_id,
                ruleidsarray,
            });
            if (data?.returncode === 0) {
                return {
                    success: true,
                    message: data.message,
                    refresh: data.refreshcurrentpage,
                };
            } else {
                const errorMsg = data?.message;
                commit("SET_ERROR", errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to remove rule(s)")
                : error.message || "Failed to remove rule(s)";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    allRules: (state) => state.rules,
    singleRule: (state) => state.singleRule,
    isRulesLoading: (state) => state.loading,
    getRulesError: (state) => state.error,
    totalRules: (state) => state.totalRules,
    schedulingPreferences: (state) => state.schedulingPreferences,
};

export default {
    state,
    mutations,
    actions,
    getters,
};
